import React, { useState } from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  Input,
  Modal,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import type { PvpInningsTotal, PvpMatchDetailDto, PvpSide } from '@/types/pvp';
import { PlayerName } from './MatchesTable';
import {
  abortCauseText,
  ballLogNote,
  ballRows,
  canEndMatch,
  clientLabel,
  coinRows,
  END_NOTE_MAX,
  endNoteError,
  endRequestLine,
  fmtDuration,
  fmtNum,
  payoutLabel,
  playerLabel,
  ratingChange,
  reasonLabel,
  resultLabel,
  syncRows,
  whenLabel,
  type BallTableRow,
  type CoinTableRow,
  type DrawerState,
  type SyncTableRow,
} from './model';

const { Text, Title } = Typography;

const SIDES: PvpSide[] = ['A', 'B'];

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginTop: 20 }}>
    <Title level={5} style={{ marginBottom: 8 }}>
      {title}
    </Title>
    {children}
  </div>
);

const SYNC_COLUMNS: ColumnsType<SyncTableRow> = [
  { title: 'What', dataIndex: 'label', key: 'label' },
  { title: 'Match', dataIndex: 'match', key: 'match', align: 'right' },
  { title: 'A', dataIndex: 'A', key: 'A', align: 'right' },
  { title: 'B', dataIndex: 'B', key: 'B', align: 'right' },
];

const COIN_COLUMNS: ColumnsType<CoinTableRow> = [
  { title: 'Side', dataIndex: 'side', key: 'side' },
  { title: 'Player', dataIndex: 'player', key: 'player' },
  { title: 'Source', dataIndex: 'source', key: 'source' },
  { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right' },
  { title: 'Time (IST)', dataIndex: 'at', key: 'at' },
];

const BALL_COLUMNS: ColumnsType<BallTableRow> = [
  { title: 'n', dataIndex: 'n', key: 'n' },
  { title: 'Inn', dataIndex: 'inn', key: 'inn' },
  { title: 'Ball', dataIndex: 'ball', key: 'ball' },
  { title: 'Runs', dataIndex: 'runs', key: 'runs', align: 'right' },
  { title: 'Extras', dataIndex: 'extras', key: 'extras', align: 'right' },
  { title: 'Legal', dataIndex: 'legal', key: 'legal' },
  { title: 'Wicket', dataIndex: 'wicket', key: 'wicket' },
];

const INNINGS_COLUMNS: ColumnsType<PvpInningsTotal> = [
  { title: 'Inn', dataIndex: 'inn', key: 'inn' },
  { title: 'Round', dataIndex: 'round', key: 'round' },
  { title: 'Batting', dataIndex: 'batting', key: 'batting' },
  {
    title: 'Score',
    key: 'score',
    render: (_, i) => `${fmtNum(i.runs, 0)}/${fmtNum(i.wkts, 0)}`,
  },
  {
    title: 'Legal balls',
    key: 'legalBalls',
    align: 'right',
    render: (_, i) => fmtNum(i.legalBalls, 0),
  },
  { title: 'Target', key: 'target', align: 'right', render: (_, i) => fmtNum(i.target, 0) },
];

interface BodyProps {
  state: DrawerState;
  onOpenPlayer: (userId: string) => void;
  /** The admin may edit players (the end route needs players:edit). */
  canEnd?: boolean;
  /** Sends the end request; the refusal's message, or null when it was made. */
  onEnd?: (note: string) => Promise<string | null>;
}

/**
 * Once asked, the request and what became of it. A live match nobody has asked
 * to end, or whose request failed: "End and refund" opens a note box and sends
 * the request.
 */
const EndMatch: React.FC<{
  m: PvpMatchDetailDto;
  canEnd: boolean;
  onEnd?: (note: string) => Promise<string | null>;
}> = ({ m, canEnd, onEnd }) => {
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);

  const line = m.endRequest ? endRequestLine(m.endRequest) : null;
  const status = line && (
    <Alert type={line.type} showIcon message={line.text} style={{ marginTop: 12 }} />
  );
  if (!canEndMatch(m) || !canEnd || !onEnd) return status;

  const noteError = endNoteError(note);
  const send = async () => {
    setSending(true);
    const error = await onEnd(note);
    setSending(false);
    setRefused(error);
    if (!error) {
      setAsking(false);
      setNote('');
    }
  };

  return (
    <>
      {status}
      <Button danger style={{ marginTop: 12 }} onClick={() => setAsking(true)}>
        End and refund
      </Button>
      <Modal
        open={asking}
        title="End this match and refund both players?"
        okText="End match"
        okButtonProps={{ danger: true, disabled: noteError !== null, loading: sending }}
        onOk={send}
        onCancel={() => {
          setAsking(false);
          setRefused(null);
        }}
      >
        <Typography.Paragraph>
          A PvP server ends it within about 10 s as a no-fault abort: both entry fees go back, no
          rating changes, and both players' games end the match as aborted. Use this only for a
          match that is stuck.
        </Typography.Paragraph>
        <Input.TextArea
          aria-label="Why"
          placeholder="Why (kept with the request and in the audit log)"
          value={note}
          maxLength={END_NOTE_MAX}
          showCount
          autoSize={{ minRows: 2, maxRows: 4 }}
          onChange={(e) => setNote(e.target.value)}
        />
        {refused && <Alert type="error" showIcon message={refused} style={{ marginTop: 12 }} />}
      </Modal>
    </>
  );
};

const Summary: React.FC<{ m: PvpMatchDetailDto }> = ({ m }) => {
  const cause = abortCauseText(m.abortCause, m.players);
  return (
    <Descriptions size="small" column={2} bordered>
      <Descriptions.Item label="Status">{m.status}</Descriptions.Item>
      <Descriptions.Item label="Reason">{reasonLabel(m.reason)}</Descriptions.Item>
      <Descriptions.Item label="Winner">{resultLabel(m)}</Descriptions.Item>
      <Descriptions.Item label="Bot match">{m.isBot ? 'Yes' : 'No'}</Descriptions.Item>
      {cause && (
        <Descriptions.Item label="Abort cause" span={2}>
          {cause}
        </Descriptions.Item>
      )}
      <Descriptions.Item label="Created">{whenLabel(m.createdAt, true)}</Descriptions.Item>
      <Descriptions.Item label="Started">{whenLabel(m.startedAt, true)}</Descriptions.Item>
      <Descriptions.Item label="Ended">{whenLabel(m.endedAt, true)}</Descriptions.Item>
      <Descriptions.Item label="Duration">{fmtDuration(m.durationSec)}</Descriptions.Item>
      <Descriptions.Item label="Sim version">{fmtNum(m.simVersion, 0)}</Descriptions.Item>
      <Descriptions.Item label="Proto">{fmtNum(m.proto, 0)}</Descriptions.Item>
      <Descriptions.Item label="Core id" span={2}>
        {m.coreId ? <Text code>{m.coreId}</Text> : '—'}
      </Descriptions.Item>
      <Descriptions.Item label="Entry fee">{fmtNum(m.entryFee, 0)}</Descriptions.Item>
      <Descriptions.Item label="Payout (A / B)">{payoutLabel(m.payout)}</Descriptions.Item>
      <Descriptions.Item label="Load failures">
        {(m.loadFailures ?? []).length === 0 ? 'None' : (m.loadFailures ?? []).join(', ')}
      </Descriptions.Item>
      <Descriptions.Item label="Grace used">
        {(m.graced ?? []).length === 0 ? 'None' : (m.graced ?? []).join(', ')}
      </Descriptions.Item>
    </Descriptions>
  );
};

const PlayersSection: React.FC<{
  m: PvpMatchDetailDto;
  onOpenPlayer: (userId: string) => void;
}> = ({ m, onOpenPlayer }) => (
  <Table
    size="small"
    pagination={false}
    rowKey="side"
    dataSource={SIDES.map((side) => ({ side }))}
    columns={[
      { title: 'Side', dataIndex: 'side', key: 'side' },
      {
        title: 'Player',
        key: 'player',
        render: (_: unknown, r: { side: PvpSide }) => (
          <PlayerName player={m.players?.[r.side]} onOpenPlayer={onOpenPlayer} />
        ),
      },
      {
        title: 'Rating',
        key: 'rating',
        render: (_: unknown, r: { side: PvpSide }) => ratingChange(m.rating?.[r.side]),
      },
      {
        title: 'App',
        key: 'client',
        render: (_: unknown, r: { side: PvpSide }) => clientLabel(m.clients?.[r.side]),
      },
      {
        title: 'Payout',
        key: 'payout',
        align: 'right' as const,
        render: (_: unknown, r: { side: PvpSide }) =>
          m.payout == null ? '—' : fmtNum(m.payout[r.side], 0),
      },
    ]}
  />
);

/** The ball log section: its own spinner, then the table, or why there is none. */
const BallLog: React.FC<{ state: DrawerState }> = ({ state }) => {
  if (state.ballsLoading || state.balls == null) {
    return <Spin size="small" />;
  }
  const note = ballLogNote(state.balls);
  const rows = ballRows(state.balls);
  return (
    <>
      {note && (
        <Alert
          type={state.balls.available ? 'info' : 'warning'}
          showIcon
          message={note}
          style={{ marginBottom: 8 }}
        />
      )}
      {state.balls.available && (
        <Table<BallTableRow>
          size="small"
          rowKey="key"
          columns={BALL_COLUMNS}
          dataSource={rows}
          pagination={false}
          scroll={{ y: 360 }}
          data-source={state.balls.source ?? undefined}
        />
      )}
    </>
  );
};

/** What the match drawer shows for the state it is given. */
export const MatchDetailView: React.FC<BodyProps> = ({
  state,
  onOpenPlayer,
  canEnd = false,
  onEnd,
}) => {
  const m = state.match;
  return (
    <>
      {state.matchError && <Alert type="error" showIcon message={state.matchError} />}
      {state.matchLoading && <Spin style={{ display: 'block', margin: '24px auto' }} />}
      {m && (
        <>
          <Text type="secondary" copyable={{ text: m.id }} style={{ fontSize: 12 }}>
            {m.id}
          </Text>
          <EndMatch m={m} canEnd={canEnd} onEnd={onEnd} />
          <Section title="Summary">
            <Summary m={m} />
          </Section>
          <Section title="Players">
            <PlayersSection m={m} onOpenPlayer={onOpenPlayer} />
          </Section>
          <Section title="Innings">
            {(m.innings ?? []).length === 0 ? (
              <Text type="secondary">No innings recorded.</Text>
            ) : (
              <Table<PvpInningsTotal>
                size="small"
                rowKey="inn"
                columns={INNINGS_COLUMNS}
                dataSource={m.innings}
                pagination={false}
              />
            )}
          </Section>
          <Section title="Coins">
            {coinRows(m).length === 0 ? (
              <Text type="secondary">No coins moved for this match.</Text>
            ) : (
              <Table<CoinTableRow>
                size="small"
                columns={COIN_COLUMNS}
                dataSource={coinRows(m)}
                pagination={false}
              />
            )}
          </Section>
          <Section title="Sync">
            {m.sync == null ? (
              <Text>No sync row</Text>
            ) : (
              <>
                <Text>
                  {fmtNum(m.sync.balls, 0)} balls checked ·{' '}
                  <Link to="/pvp-sync">Open PvP Sync</Link>
                </Text>
                <Table<SyncTableRow>
                  size="small"
                  style={{ marginTop: 8 }}
                  columns={SYNC_COLUMNS}
                  dataSource={syncRows(m.sync.summary)}
                  pagination={false}
                />
                <details style={{ marginTop: 8 }}>
                  <summary>Raw summary</summary>
                  <pre style={{ fontSize: 11, maxHeight: 240, overflow: 'auto' }}>
                    {JSON.stringify(m.sync.summary, null, 2)}
                  </pre>
                </details>
              </>
            )}
          </Section>
        </>
      )}
      {(m || state.ballsLoading || state.balls) && (
        <Section title="Ball log">
          <BallLog state={state} />
        </Section>
      )}
    </>
  );
};

interface DrawerProps extends BodyProps {
  open: boolean;
  onClose: () => void;
}

export const MatchDrawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  state,
  onOpenPlayer,
  canEnd,
  onEnd,
}) => (
  <Drawer
    open={open}
    onClose={onClose}
    width={760}
    destroyOnClose
    title={
      state.match ? (
        <span>
          {playerLabel(state.match.players?.A)} vs {playerLabel(state.match.players?.B)}{' '}
          <Tag>{state.match.status}</Tag>
        </span>
      ) : (
        'Match'
      )
    }
  >
    <MatchDetailView state={state} onOpenPlayer={onOpenPlayer} canEnd={canEnd} onEnd={onEnd} />
  </Drawer>
);
